/**
 * Importação em massa por planilha (Excel/CSV) — tela dedicada `/cadastro/importar/:recurso`.
 *
 * Três fases numa máquina de estados simples:
 *  1. upload   — escolhe o arquivo + descreve (opcional); a IA (ou um molde de layout) sugere o mapa.
 *  2. revisao  — grade coluna→campo: o operador confere a amostra e ajusta cada atribuição; pode
 *                salvar o layout como molde reutilizável. Confirmar enfileira a importação.
 *  3. progresso — polling do estado: barra + contadores (criadas/reusadas/contatos/sócios/erros) até
 *                concluir. Cada linha ruim é isolada no back; os erros aparecem aqui em detalhe.
 *
 * O trabalho pesado é assíncrono (worker); esta tela só orquestra o upload, a revisão e o polling.
 */
import { ArrowLeft, FileSpreadsheet, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ComboReferencia } from '@/components/crud/ComboReferencia';
import { ProgressoImportacao } from '@/components/crud/ProgressoImportacao';
import { SeletorTags } from '@/components/crud/SeletorTags';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { Tabela, Tbody, Td, Th, Thead, Tr } from '@/components/ui/tabela';
import {
  type AnaliseImportacao,
  analisarPlanilha,
  confirmarImportacao,
  type EstadoImportacao,
  statusImportacao,
} from '@/lib/api';
import { obterRecurso } from '@/lib/recursos';

const IGNORAR = 'ignorar';

//: Colunas mapeadas para telefone/e-mail podem ter vários valores por célula (com um separador).
const ehContato = (chave: string | undefined) => chave === 'telefone' || chave === 'email';

/**
 * Campos que receberam MAIS DE UMA coluna — e para os quais isso é um erro.
 *
 * Nasceu de um caso concreto: num estoque de FIDC a sugestão automática mandou `DATA_FUNDO` (30/06)
 * e `DATA_REFERENCIA` (29/06) para o mesmo campo. Uma das duas vencia em silêncio e a carteira
 * inteira ficava datada de um dia que ninguém escolheu.
 *
 * Contato fica de fora de propósito: TELEFONE1 e TELEFONE2 nos dois no mesmo campo é o uso normal —
 * eles se acumulam em vez de disputar. Marcar isso como problema treinaria o operador a ignorar o
 * aviso, que é o jeito mais rápido de um aviso deixar de servir.
 */
function camposRepetidos(atribuicoes: Record<string, string>): Map<string, string[]> {
  const por_chave = new Map<string, string[]>();
  for (const [coluna, chave] of Object.entries(atribuicoes)) {
    if (!chave || chave === IGNORAR || ehContato(chave)) continue;
    por_chave.set(chave, [...(por_chave.get(chave) ?? []), coluna]);
  }
  return new Map([...por_chave].filter(([, cols]) => cols.length > 1));
}

/** Uma prévia do conteúdo de uma coluna (primeiros valores não-vazios da amostra). */
function previa(amostra: Record<string, string>[], coluna: string): string {
  const vals = amostra.map((l) => l[coluna]).filter((v) => v && v.trim());
  return vals.slice(0, 3).join(' · ') || '—';
}

export function ImportarPlanilha() {
  const { recurso } = useParams();
  const navegar = useNavigate();
  const descritor = obterRecurso(recurso);

  const [fase, setFase] = useState<'upload' | 'revisao' | 'progresso'>('upload');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [prompt, setPrompt] = useState('');
  const [analisando, setAnalisando] = useState(false);

  const [analise, setAnalise] = useState<AnaliseImportacao | null>(null);
  const [atribuicoes, setAtribuicoes] = useState<Record<string, string>>({});
  // Valores de texto fixos aplicados a toda linha (ex.: { porte: 'ME' }).
  const [constantes, setConstantes] = useState<Record<string, string>>({});
  // Referência (segmento/profissão) fixa aplicada a toda linha — id (mesmo ComboReferencia do cadastro).
  const [referenciaFixa, setReferenciaFixa] = useState('');
  // Colunas de contato com VÁRIOS valores por célula → separador (ex.: { Telefones: '|' }).
  const [separadores, setSeparadores] = useState<Record<string, string>>({});
  // Tags aplicadas a TODOS os registros do lote (0..N ids; combo com criar na hora).
  const [tagsLote, setTagsLote] = useState<string[]>([]);
  const [salvarMolde, setSalvarMolde] = useState(false);
  const [nomeMolde, setNomeMolde] = useState('');
  const [confirmando, setConfirmando] = useState(false);

  const [estado, setEstado] = useState<EstadoImportacao | null>(null);
  const importacaoIdRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const cfg = descritor?.importarPlanilha;
  const repetidos = camposRepetidos(atribuicoes);

  const analisar = useCallback(async () => {
    if (!arquivo || !cfg) return;
    setAnalisando(true);
    const r = await analisarPlanilha(cfg.endpoint, arquivo, prompt);
    setAnalisando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    setAnalise(r.dados);
    setAtribuicoes(r.dados.atribuicoes);
    setConstantes(r.dados.constantes ?? {});
    setReferenciaFixa(r.dados.referencia_id ?? '');
    setSeparadores(r.dados.separadores ?? {});
    importacaoIdRef.current = r.dados.importacao_id;
    setFase('revisao');
    const msg =
      r.dados.via === 'molde'
        ? 'Layout reconhecido — mapa preenchido por um molde salvo.'
        : r.dados.via === 'manual'
          ? `${r.dados.total_linhas} linhas lidas. IA indisponível — mapeie as colunas à mão.`
          : `${r.dados.total_linhas} linhas lidas. Confira o mapa sugerido pela IA.`;
    if (r.dados.via === 'manual') toast.info(msg);
    else toast.success(msg);
  }, [arquivo, cfg, prompt]);

  const confirmar = useCallback(async () => {
    const id = importacaoIdRef.current;
    if (!id) return;
    if (salvarMolde && !nomeMolde.trim()) {
      toast.error('Dê um nome ao molde ou desmarque a opção.');
      return;
    }
    setConfirmando(true);
    // Só manda constantes com valor de fato preenchido.
    const fixos = Object.fromEntries(
      Object.entries(constantes).filter(([, v]) => v.trim()),
    );
    // Só manda separadores das colunas que continuam mapeadas para contato (telefone/email).
    const seps = Object.fromEntries(
      Object.entries(separadores).filter(
        ([col, sep]) => sep.trim() && ehContato(atribuicoes[col]),
      ),
    );
    const r = await confirmarImportacao(
      id,
      atribuicoes,
      fixos,
      referenciaFixa,
      seps,
      tagsLote,
      salvarMolde ? { nome: nomeMolde.trim() } : undefined,
    );
    setConfirmando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    setFase('progresso');
  }, [atribuicoes, constantes, referenciaFixa, separadores, tagsLote, salvarMolde, nomeMolde]);

  // Polling do progresso enquanto processa. Para ao concluir/errar; limpa no desmonte.
  useEffect(() => {
    if (fase !== 'progresso') return;
    const id = importacaoIdRef.current;
    if (!id) return;
    let vivo = true;
    let timer: ReturnType<typeof setTimeout>;
    const bater = async () => {
      const r = await statusImportacao(id);
      if (!vivo) return;
      if (r.ok) {
        setEstado(r.dados);
        if (r.dados.estado === 'concluida' || r.dados.estado === 'erro') return;
      }
      timer = setTimeout(bater, 1200);
    };
    void bater();
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, [fase]);

  if (!descritor) return <Navigate to="/cadastro" replace />;
  if (!cfg) return <Navigate to={`/cadastro/${descritor.chave}`} replace />;

  // Rótulos CURTOS para o select (o que aparece inteiro na coluna "Corresponde a").
  const opcoesChave = analise ? Object.entries(analise.rotulos) : [];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start gap-3">
        <Button
          variant="fantasma"
          size="icon"
          onClick={() => navegar(`/cadastro/${descritor.chave}`)}
          aria-label="Voltar"
        >
          <ArrowLeft />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="size-5 text-accent" />
            <h1 className="tipo-titulo">Importar planilha · {descritor.titulo}</h1>
          </div>
          <p className="mt-1 tipo-corpo-grande text-text-secondary">
            {cfg.descricao ?? 'Suba um Excel ou CSV e importe muitos registros de uma vez.'}
          </p>
        </div>
      </header>

      {fase === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>1. Arquivo e contexto</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border-strong bg-input py-10 text-center transition-colors hover:bg-surface-hover"
            >
              <Upload className="size-6 text-text-tertiary" />
              <span className="tipo-corpo text-text">
                {arquivo ? arquivo.name : 'Clique para escolher um Excel (.xlsx) ou CSV'}
              </span>
              {arquivo && (
                <span className="tipo-rotulo text-text-tertiary">
                  {(arquivo.size / 1024).toFixed(0)} KB
                </span>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xlsm,.xls,.csv,.txt,.tsv"
              className="hidden"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prompt-planilha" className="tipo-rotulo text-text-secondary">
                Descreva a planilha (opcional) — ajuda a IA a mapear as colunas
              </label>
              <textarea
                id="prompt-planilha"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder="Ex.: são imobiliárias de Porto Alegre; a coluna 'Responsável' é o sócio principal."
                className="w-full rounded-md border border-border bg-input px-3 py-2 text-[15px] text-text placeholder:text-text-tertiary focus-visible:border-border-strong focus-visible:shadow-(--focus-ring) focus-visible:outline-none"
              />
            </div>
            <div className="flex justify-end">
              <Button variant="primario" onClick={analisar} disabled={!arquivo || analisando}>
                {analisando ? <Spinner /> : <FileSpreadsheet />}
                {analisando ? 'Analisando…' : 'Analisar planilha'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {fase === 'revisao' && analise && (
        <Card>
          <CardHeader>
            <CardTitle>2. Revise o mapa das colunas</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="tipo-corpo text-text-secondary">
              {analise.total_linhas} linhas. Para cada coluna, confirme a que campo ela corresponde —
              ou marque <span className="text-text">Ignorar</span>. A amostra ajuda a conferir.
            </p>
            <p className="tipo-rotulo text-text-tertiary">
              Registros com {analise.chave_dedup} já cadastrado são{' '}
              <span className="text-text">atualizados</span> (os campos vazios são preenchidos e os
              contatos acumulados) — nunca duplicados.
            </p>
            {repetidos.size > 0 && (
              // Nomeia as duas colunas, porque quem escolhe entre elas é o operador: só ele sabe
              // qual das duas datas é a do arquivo. A tela avisa; o servidor recusa.
              <div className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/10 p-3">
                <p className="tipo-rotulo text-warning">Duas colunas para o mesmo campo</p>
                {[...repetidos].map(([chave, cols]) => (
                  <p key={chave} className="tipo-corpo-pequeno text-text">
                    <span className="text-text-secondary">{analise.rotulos[chave] ?? chave}</span>{' '}
                    recebeu <span className="tipo-dado">{cols.join(' e ')}</span>
                  </p>
                ))}
                <p className="tipo-corpo-pequeno text-text-tertiary">
                  Só uma vale — a outra seria descartada em silêncio. Deixe uma delas em Ignorar.
                </p>
              </div>
            )}
            <div className="overflow-x-auto">
              <Tabela>
                <Thead>
                  <Th>Coluna da planilha</Th>
                  <Th>Amostra</Th>
                  <Th>Corresponde a</Th>
                </Thead>
                <Tbody>
                  {analise.colunas.map((col) => (
                    <Tr key={col}>
                      <Td mono>{col}</Td>
                      <Td>
                        <span className="tipo-rotulo text-text-tertiary">
                          {previa(analise.amostra, col)}
                        </span>
                      </Td>
                      <Td>
                        <select
                          value={atribuicoes[col] ?? IGNORAR}
                          title={analise.chaves[atribuicoes[col]] ?? undefined}
                          onChange={(e) =>
                            setAtribuicoes((a) => ({ ...a, [col]: e.target.value }))
                          }
                          className={`h-10 w-full min-w-56 rounded-md border bg-input px-3 text-[15px] text-text focus-visible:border-border-strong focus-visible:outline-none ${
                            repetidos.has(atribuicoes[col]) ? 'border-warning' : 'border-border'
                          }`}
                        >
                          <option value={IGNORAR}>— Ignorar —</option>
                          {opcoesChave.map(([chave, rotulo]) => (
                            <option key={chave} value={chave}>
                              {rotulo}
                            </option>
                          ))}
                        </select>
                        {ehContato(atribuicoes[col]) && (
                          <label className="mt-1.5 flex items-center gap-1.5">
                            <span className="tipo-rotulo whitespace-nowrap text-text-tertiary">
                              vários por célula? separador
                            </span>
                            <input
                              value={separadores[col] ?? ''}
                              onChange={(e) =>
                                setSeparadores((s) => ({ ...s, [col]: e.target.value }))
                              }
                              placeholder="ex.: |"
                              maxLength={4}
                              className="h-8 w-16 rounded-md border border-border bg-input px-2 text-center text-[15px] text-text focus-visible:border-border-strong focus-visible:outline-none"
                            />
                          </label>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Tabela>
            </div>

            <div className="flex flex-col gap-2 rounded-md border border-border bg-input/40 p-4">
              <div>
                <p className="tipo-rotulo text-text-secondary">Tags do lote (opcional)</p>
                <p className="tipo-rotulo text-text-tertiary">
                  Aplica uma ou mais tags a TODOS os registros importados — depois é só filtrar a
                  lista por elas para reencontrar este lote. Crie na hora (ex.: "Administradoras RS",
                  "Leva jul/2026").
                </p>
              </div>
              <SeletorTags valor={tagsLote} onChange={setTagsLote} />
            </div>

            <div className="flex flex-col gap-3 rounded-md border border-border bg-input/40 p-4">
              <div>
                <p className="tipo-rotulo text-text-secondary">
                  Valores fixos para TODOS os registros (opcional)
                </p>
                <p className="tipo-rotulo text-text-tertiary">
                  Defina aqui um valor uma vez quando vale para o arquivo inteiro. Onde houver coluna
                  com o mesmo dado, a coluna vence.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {analise.referencia.endpoint && (
                  <div className="flex flex-col gap-1">
                    <label htmlFor="fixo-referencia" className="tipo-rotulo text-text-secondary">
                      {analise.referencia.rotulo} (para todos)
                    </label>
                    <ComboReferencia
                      id="fixo-referencia"
                      valor={referenciaFixa}
                      onChange={setReferenciaFixa}
                      endpoint={analise.referencia.endpoint}
                      permitirCriar
                    />
                  </div>
                )}
                {Object.entries(analise.chaves_constante).map(([chave, desc]) => {
                  const opcoes = analise.opcoes_constante?.[chave];
                  return (
                    <div key={chave} className="flex flex-col gap-1">
                      <label htmlFor={`fixo-${chave}`} className="tipo-rotulo text-text-secondary">
                        {desc}
                      </label>
                      {opcoes ? (
                        <select
                          id={`fixo-${chave}`}
                          value={constantes[chave] ?? ''}
                          onChange={(e) => setConstantes((c) => ({ ...c, [chave]: e.target.value }))}
                          className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
                        >
                          <option value="">— não aplicar —</option>
                          {opcoes.map((o) => (
                            <option key={o} value={o}>
                              {o.charAt(0).toUpperCase() + o.slice(1)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          id={`fixo-${chave}`}
                          value={constantes[chave] ?? ''}
                          onChange={(e) => setConstantes((c) => ({ ...c, [chave]: e.target.value }))}
                          placeholder="deixe vazio para não aplicar"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center gap-2 tipo-corpo text-text">
              <input
                type="checkbox"
                checked={salvarMolde}
                onChange={(e) => setSalvarMolde(e.target.checked)}
                className="size-4 accent-primary"
              />
              Salvar este mapa como molde (reusa automaticamente em planilhas com as mesmas colunas)
            </label>
            {salvarMolde && (
              <Input
                value={nomeMolde}
                onChange={(e) => setNomeMolde(e.target.value)}
                placeholder="Nome do molde — ex.: Imobiliárias RS (layout do parceiro X)"
                className="max-w-md"
              />
            )}

            <div className="flex justify-between">
              <Button variant="secundario" onClick={() => setFase('upload')}>
                Voltar
              </Button>
              <Button variant="primario" onClick={confirmar} disabled={confirmando}>
                {confirmando ? <Spinner /> : <Upload />}
                Confirmar e importar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {fase === 'progresso' && (
        <ProgressoImportacao
          titulo="3. Importando"
          estado={estado}
          rotuloVinculos="Sócios"
          acoes={
            <>
              <Button
                variant="secundario"
                onClick={() => {
                  setFase('upload');
                  setArquivo(null);
                  setPrompt('');
                  setAnalise(null);
                  setEstado(null);
                  importacaoIdRef.current = null;
                }}
              >
                Importar outra
              </Button>
              <Button variant="primario" onClick={() => navegar(`/cadastro/${descritor.chave}`)}>
                Ver {descritor.titulo}
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}
